import { Card, CardContent, CardActionArea, Typography, Box, Chip, Divider, CardMedia } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import TerrainIcon from '@mui/icons-material/Terrain';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DiamondIcon from '@mui/icons-material/Diamond';
import GrainIcon from '@mui/icons-material/Grain';

const LandDeedItem = ({ deed, onDeedClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleClick = () => {
    if (onDeedClick) {
      onDeedClick(deed);
    }
  };

  // Helper function to get rarity color
  const getRarityColor = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'common': return colors.grey[500];
      case 'rare': return colors.blueAccent[500];
      case 'epic': return '#9c27b0'; // Violett für Epic
      case 'legendary': return '#ffd700'; // Gold für Legendary
      case 'mystic': return '#ff8c00'; // Orange für Mystic
      default: return colors.grey[400];
    }
  };

  // Helper function to format price
  const formatPrice = (price) => {
    const numPrice = parseFloat(price || 0);
    if (numPrice >= 1000) {
      return `$${(numPrice / 1000).toFixed(1)}k`;
    }
    return `$${numPrice.toFixed(2)}`;
  };

  // Helper function to get territory display name
  const getTerritoryDisplay = (territory) => {
    if (!territory) return 'Unknown';
    return territory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Generate full land ID in Splinterlands format: PR-GLL-114-05-016
  const getFullLandId = (deed) => {
    if (!deed) return 'Unknown';
    const regionUid = deed.region_uid || `R${deed.region_id || '?'}`;
    const tractId = String(deed.tract_id || 0).padStart(2, '0');
    const plotId = String(deed.plot_id || 0).padStart(3, '0');
    return `${regionUid}-${tractId}-${plotId}`;
  };

  // Helper function to get land image URL based on territory/rarity
  const getLandImageUrl = (deed) => {
    // You can customize this logic based on your actual image assets
    const territory = deed.territory?.toLowerCase() || 'unknown';
    const rarity = deed.rarity?.toLowerCase() || 'common';
    
    // Example image URL structure - adjust based on your actual image assets
    return `https://d36mxiodymuqjm.cloudfront.net/land/${territory}_${rarity}.webp`;
  };

  // Fallback placeholder image generator
  const generatePlaceholderImage = (deed) => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 225; // 16:9 aspect ratio
    const ctx = canvas.getContext('2d');
    
    // Background gradient based on rarity
    const rarityColor = getRarityColor(deed.rarity);
    const gradient = ctx.createLinearGradient(0, 0, 400, 225);
    gradient.addColorStop(0, rarityColor + '20');
    gradient.addColorStop(1, rarityColor + '05');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 400, 225);
    
    // Add territory text
    ctx.fillStyle = rarityColor;
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(getTerritoryDisplay(deed.territory), 200, 120);
    
    return canvas.toDataURL();
  };

  return (
    <Card
      sx={{
        backgroundColor: colors.primary[400],
        border: `1px solid ${colors.primary[300]}`,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: colors.greenAccent[400],
          boxShadow: `0 8px 25px rgba(${theme.palette.mode === 'dark' ? '255,255,255' : '0,0,0'}, 0.15)`
        }
      }}
    >
      <CardActionArea onClick={handleClick} sx={{ height: '100%' }}>
        {/* Large Land Image */}
        <CardMedia
          component="img"
          height="160"
          image={getLandImageUrl(deed)}
          alt={`${getTerritoryDisplay(deed.territory)} Land`}
          sx={{
            objectFit: 'cover',
            background: `linear-gradient(135deg, ${getRarityColor(deed.rarity)}20, ${getRarityColor(deed.rarity)}05)`,
            borderBottom: `2px solid ${getRarityColor(deed.rarity)}`,
          }}
          onError={(e) => {
            // Fallback to placeholder image if main image fails to load
            e.target.src = generatePlaceholderImage(deed);
          }}
        />

        <CardContent sx={{ p: 2, height: 'calc(100% - 160px)', display: 'flex', flexDirection: 'column' }}>
          {/* Header with map and full land ID */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <TerrainIcon sx={{ color: colors.greenAccent[400], fontSize: '1.2rem' }} />
              <Typography variant="body2" color={colors.grey[200]} fontWeight="bold">
                {deed.map_name || 'Unknown Map'}
              </Typography>
            </Box>
          </Box>

          {/* Full Land ID in Splinterlands format */}
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <LocationOnIcon sx={{ color: colors.grey[400], fontSize: '1rem' }} />
            <Typography variant="caption" color={colors.blueAccent[300]} fontWeight="bold">
              {getFullLandId(deed)}
            </Typography>
          </Box>

          {/* Territory */}
          <Typography variant="body2" color={colors.grey[200]} mb={1} fontWeight="medium">
            {getTerritoryDisplay(deed.territory)}
          </Typography>

          {/* Rarity */}
          {deed.rarity && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <DiamondIcon sx={{ color: getRarityColor(deed.rarity), fontSize: '1rem' }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: getRarityColor(deed.rarity),
                  fontWeight: 'bold',
                  textTransform: 'capitalize'
                }}
              >
                {deed.rarity}
              </Typography>
            </Box>
          )}

          {/* Resource Type */}
          {deed.resource_type && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <GrainIcon sx={{ color: colors.greenAccent[400], fontSize: '1rem' }} />
              <Typography variant="caption" color={colors.greenAccent[300]}>
                {deed.resource_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Typography>
            </Box>
          )}

          {/* Land Stats if available */}
          {deed.land_stats && (
            <Box mb={2}>
              <Typography variant="caption" color={colors.grey[400]} sx={{ display: 'block', mb: 0.5 }}>
                Stats:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {Object.entries(deed.land_stats).slice(0, 3).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    size="small"
                    sx={{
                      backgroundColor: colors.primary[300],
                      color: colors.grey[200],
                      fontSize: '0.6rem',
                      height: '18px'
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ borderColor: colors.primary[300], my: 1 }} />

          {/* Price */}
          <Box mt="auto">
            <Typography variant="h6" color={colors.greenAccent[400]} fontWeight="bold" textAlign="center">
              {formatPrice(deed.listing_price)}
            </Typography>
            <Typography variant="caption" color={colors.grey[400]} textAlign="center" display="block">
              {deed.listing_currency || 'USD'}
            </Typography>
          </Box>

          {/* Deed Type */}
          {deed.deed_type && (
            <Box mt={1}>
              <Chip
                label={deed.deed_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                size="small"
                sx={{
                  backgroundColor: colors.redAccent[700],
                  color: colors.grey[100],
                  fontSize: '0.7rem',
                  width: '100%'
                }}
              />
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default LandDeedItem;
